import { OptInStatus } from '@app/constants/opt-in-status';

export const formatInit = (action) => `REQUEST_${action}_INIT`;
export const formatSuccess = (action) => `REQUEST_${action}_SUCCESS`;
export const formatError = (action) => `REQUEST_${action}_ERROR`;

export const CREATION_SOURCE = 'WebApp';

const GET_MAP = {
  CLAIM: 'claim',
  POLICY: 'policy',
  USER: 'user/userlist',
  OPERATOR: 'user/userlist?role=operator&includeGroupDetails=true',
  CASE: 'job/query?view=jobs',
  GROUP: 'groups',
  QUEUE: 'queue',
  SUBGROUP: 'groups',
  SEARCHGROUPS: 'groups',
};
export const GET_ITEMS = (type, query) => {
  let baseUrl = `/api/${GET_MAP[type]}`;
  if (query) {
    baseUrl += baseUrl.indexOf('?') > -1 ? '&' : '?';
    baseUrl += Object.keys(query)
      .map((p) =>
        Array.isArray(query[p])
          ? `${p}[]=${query[p].join(`&${p}[]=`)}`
          : `${encodeURIComponent(p)}=${encodeURIComponent(query[p])}`,
      )
      .join('&');
  }
  return baseUrl;
};

const POST_MAP = {
  POLICY: 'policy',
  CLAIM: 'claim',
  CASE: 'job',
  USER: 'user/create-user',
  GROUP: 'groups',
  QUEUE: 'queue',
};

// Generic
export const CREATE_ITEM = (type) => `/api/${POST_MAP[type]}`;
export const UPDATE_ITEM = (type) => `/api/${POST_MAP[type]}`;
export const CHECK_USER = (phone) => `/api/profile/verifyphonenumber/${phone}`;

// Other
export const DESCRIBE_SERVICE = (serviceName) =>
  `/api/describeService/${serviceName}`;
export const REOPEN_METRIC = '/dev/case';
export const CLOSE_METRIC = '/dev/case';

// Authentication
export const GET_USER_BY_OKTA_USER_ID_ENDPOINT = (oktaUserId) =>
  `/api/user/oktaUserId/${oktaUserId}`;

export const CHANGE_PASSWORD_ENDPOINT = '/api/auth/changepassword';
export const RESET_PASSWORD_ENDPOINT = '/api/auth/resetpassword';

// User
export const GENERIC_USER_GET = '/api/user/genericGet';
export const GENERIC_USER_SET = '/api/user/genericSet';
export const DELETE_USER = (userId) => `/api/user/${userId}`;
export const DEACTIVATE_USER = (userId) => `/api/user/${userId}/deactivation`;
export const GET_CASE_ASSIGNMENT_SUMMARY = (userId) =>
  `/api/user/${userId}/case-assignment-summary`;
export const GET_USER_PROPERTIES = '/api/user/userproperties';
export const GET_USERS = '/api/user/userlist';
export const UPDATE_USER = '/api/user';
export const CREATE_OPERATOR = '/api/operators';
export const CREATE_USERS = '/api/user/create-users';
export const UPDATE_OPERATORS_UNIQUE_IDS =
  '/api/user/update-operators-unique-ids';
export const USER_PROPERTIES = '/api/user/userproperties';
export const UPDATE_USER_PROPERTIES_ENDPOINT = (userId) =>
  `${USER_PROPERTIES}?userId=${userId}`;

export const MARK_OPERATOR_ACTIVE = '/api/user/set-operator-active';
export const RECORD_OPERATOR_LOGOUT = '/api/user/logout';
export const GET_OPERATORS_BY_ORG = (orgId) => `/api/user/operators/${orgId}`;
export const PUT_AUTO_ASSIGN = (oktaUserId) =>
  `/api/user/${oktaUserId}/isAutoAssign`;
export const GET_BULK_UPLOAD_USERS_CSV_TEMPLATE = '/api/user/csv-template';
export const GET_CHANGE_UNIQUE_IDS_CSV_TEMPLATE =
  '/api/user/change-unique-ids-template';
export const GET_USERS_TO_EXPORT = '/api/user/users-to-export';
export const GET_LIST_OF_TITLES = '/api/user/titlelist';
export const GET_OPERATORS_INFO_BY_ID = '/api/user/operators-info';
export const GET_ACTIVE_OPERATORS_COUNT = '/api/user/user-count';
export const GET_BULK_UPLOAD_STATUS = '/api/user/bulk-upload-status';
export const GET_OPERATOR_DETAILS_FROM_FILE =
  '/api/operators/get-operator-details-from-emails';

// End Users
export const CREATE_END_USER = '/api/endusers';
export const UPDATE_END_USER_OPT_STATUS = (endUserId, optStatus) => {
  if (optStatus === OptInStatus.Unsubscribed) {
    // forward path endpoint (only supports opt-out for now)
    return `/api/endusers/${endUserId}/opt-status`;
  }
  return `/api/endusers/${endUserId}/opt-in`;
};
export const QUERY_END_USERS = '/api/endusers/query';
export const PARTIAL_UPDATE_END_USER = (endUserId) =>
  `/api/endusers/${endUserId}`;

// Jobs
export const CREATE_CASE = '/api/job';
export const COMPLETE_RELEASE_ISSUES = (jobId) => `/api/job/${jobId}/events`;
export const SEND_MEDIA_TO_VERISK = (jobId) =>
  `/api/job/${jobId}/syncmedia/verisk`;
export const GET_CUSTOMER_FEEDBACK = (userId) =>
  `/api/job/${encodeURI(userId)}/surveyallscore`;
export const RESEND_WELCOME_EMAIL_FOR_JOB_URL = '/api/job/welcome-mail';
export const GET_JOB = (id, creationSource) =>
  `/api/job/joblist/${id}?creationSource=${creationSource}`;
export const QUERY_JOBS = '/api/job/query';
export const CREATE_JOB = '/api/job';
export const CHAT_BOT_TAKE_OVER_ENDPOINT = (jobId) =>
  `/api/job/${jobId}/chat-bot-take-over`;
export const WRITING_ASSISTANT_SUGGESTION = (jobId) =>
  `/api/job/${jobId}/writing-assistant`;
export const RESEND_WELCOME_EMAIL = (type) =>
  `/api/${type === 'case' ? 'job' : type}/welcome-mail`;
export const SEND_SURVEY_PROXY = '/api/job/sendsurvey';
export const SET_JOB_LANGUAGE = (jobId) => `/api/job/translate/${jobId}`;
export const SET_USER_LANGUAGE = (userId) => `/api/job/translate/${userId}`;
export const PUT_JOB_ENDPOINT = (jobId) => `/api/job/${jobId}`;
export const PATCH_JOB_ENDPOINT = (id) => `/api/job/${id}`;
export const PUT_JOB_NEEDS_ACTION_RESPONSE_ENDPOINT = (
  jobId,
  chatId,
  responseType,
) => `/api/job/${jobId}/chat/${chatId}/needsActionResponse/${responseType}`;
export const BULK_UPDATE_CASES_ENDPOINT = '/api/job/bulk/update';
export const BULK_PATCH_CASES_ENDPOINT = '/api/job/bulk/patch';
export const BULK_CASE_ACTION_ENDPOINT = '/api/job/bulk/action';
export const BULK_CASE_ACTION_COUNT_ENDPOINT = '/api/job/bulk/action/count';
export const BULK_CASE_ACTION_STATUS_ENDPOINT = '/api/job/bulk/action/status';
export const BULK_CASE_ACTION_RUN_ENDPOINT = (runId, page) =>
  `/api/job/bulk/action/${runId}?page=${page}`;
export const READ_NOTE_MENTION_ENDPOINT = (jobId, noteId) =>
  `/api/job/joblist/${jobId}/notes/${noteId}/read-note-mention`;
export const PATCH_LAST_VIEWED_MESSAGE = (jobId) =>
  `/api/job/${jobId}/lastViewedMessage`;
export const PATCH_MARK_CHAT_UNREAD = (jobId) =>
  `/api/job/${jobId}/markChatUnread`;
export const PATCH_MARK_MESSAGE_UNREAD = (jobId) =>
  `/api/job/${jobId}/markMessageUnread`;
export const PUT_ACTIVE_WORKFLOW = (jobId) =>
  `/api/job/${jobId}/active-workflow`;

// Pinned Jobs
export const PIN_JOB = (operatorId) =>
  `/api/operators/${operatorId}/pinned-jobs`;
export const UNPIN_JOB = (operatorId, jobId) =>
  `/api/operators/${operatorId}/pinned-jobs/${jobId}`;

// Notes
export const CREATE_NOTE = (caseId) => `/api/job/${encodeURI(caseId)}/notes`;
export const GET_NOTES = (caseId) => `/api/job/${encodeURI(caseId)}/notes`;
export const DELETE_NOTE = ({ caseId, noteId }) =>
  `/api/job/${encodeURI(caseId)}/notes/${encodeURI(noteId)}`;
export const ADD_REPLY_ENDPOINT = (caseId, noteId) =>
  `/api/job/${encodeURI(caseId)}/notes/${encodeURI(noteId)}`;
export const DELETE_REPLY_ENDPOINT = ({ caseId, noteId, replyId }) =>
  `/api/job/${encodeURI(caseId)}/notes/${encodeURI(noteId)}/replies/${encodeURI(replyId)}`;

// Case summary
export const GET_CASE_SUMMARY = (jobId, cachedOnly = false) =>
  `/api/job/${encodeURI(jobId)}/case-summary${cachedOnly ? '?generate=false' : ''}`;
export const REGENERATE_CASE_SUMMARY = (jobId) =>
  `/api/job/${encodeURI(jobId)}/case-summary/regenerate`;

// AI feedback
export const AI_FEEDBACK = (jobId) =>
  `/api/job/${encodeURI(jobId)}/ai-feedback`;

// Profile
export const PROFILE = '/api/profile';
export const PROFILE_BY_ID = (userId) => `/api/profile/${userId}`;
export const PROFILE_IMAGE = '/api/profile/image';
export const PROFILE_AUTOREPLY = '/api/profile/replyes';

// OOO Replies
export const GET_USER_AUTOREPLY = (userId) => `/api/auto-reply/user/${userId}`;
export const CREATE_USER_AUTOREPLY = (userId) =>
  `/api/auto-reply/user/${userId}`;

// Claims
export const WELCOME_MAIL = '/api/claim/welcome-mail';

// Queue - Should we remove this?
export const GET_QUEUELIST = '/api/queue/queuelist';

// Chats
export const SEND_FILE = (chatId) => `/api/chat/${chatId}/file`;
export const FORWARD_MEDIA = (chatId) => `/api/chat/${chatId}/forward-media`;
export const ADD_USER_TO_CHAT = (chatId) =>
  `/api/chat/${encodeURI(chatId)}/adduser`;
export const REMOVE_USER_FROM_CHAT = (chatId) =>
  `/api/chat/${encodeURI(chatId)}/removeuser`;
export const GET_CHAT_MESSAGES_PAGE_V2 = (
  orgId,
  chatId,
  messageId,
  direction,
  limit,
  offset = 0,
) =>
  `/api/chat/${encodeURI(orgId)}/${encodeURI(chatId)}/messages-v2/page?id=${messageId}&direction=${direction}&limit=${limit}&offset=${offset}`;
export const GET_UNREDACTED_MESSAGE = (orgId, messageId, redactionType) =>
  `/api/message/${encodeURI(orgId)}/${encodeURI(messageId)}/${encodeURI(redactionType)}/view-body`;
export const PUT_MESSAGE_NEEDS_ACTION_RESPONSE_ENDPOINT = (
  messageId,
  jobId,
  responseType,
) =>
  `/api/message/${messageId}/job/${jobId}/needsActionResponse/${responseType}`;
export const PUT_MESSAGE_NEEDS_ATTENTION_RESPONSE_ENDPOINT = (messageId) =>
  `/api/message/${messageId}/dismissNeedsAttention`;

export const GET_CHAT_MEDIA = (chatId) =>
  `/api/chat/${encodeURI(chatId)}/mediamessages`;
export const GET_CHAT_PARTICIPANTS = (chatId) =>
  `/api/chat/${encodeURI(chatId)}/participants`;
export const GET_ALL_CHAT_PARTICIPANTS = (chatId) =>
  `/api/chat/${encodeURI(chatId)}/participants/all`;
export const SEND_MESSAGE_TO_CHAT = (chatId) => `/api/chats/${chatId}/messages`;
export const RETRY_MESSAGE = (chatId) => `/api/chats/${chatId}/retryMessage`;
export const PUT_CHAT_PARTICIPANT = (chatId) => `/api/chat/${chatId}/enduser`;

// Message Templates
export const MESSAGE_TEMPLATE_ROUTE = '/api/templates';
export const GET_KEYS = '/api/msgtemplate/keys';
export const PIN_MESSAGE_TEMPLATE_V3 = (templateId) =>
  `/api/operators/pinned-templates/${templateId}`;
export const UNPIN_MESSAGE_TEMPLATE_V3 = (templateId) =>
  `/api/operators/pinned-templates/${templateId}`;
export const SELECT_MESSAGE_TEMPLATE_V2 = (templateId) =>
  `/api/templates/${templateId}/actions/populate`;
export const QUERY_MESSAGE_TEMPLATES_V2 = (jobId, isTemplateLOB) =>
  jobId
    ? `/api/templates?jobId=${jobId}&isTemplateLOB=${isTemplateLOB}`
    : '/api/templates';
export const EXPORT_TEMPLATES = '/api/templates/export';
export const IMPORT_TEMPLATES = '/api/templates/import';
export const TEMPLATE_RECOMMENDATION_PREVIEW = (jobId) =>
  `${MESSAGE_TEMPLATE_ROUTE}/recommendation?jobId=${encodeURIComponent(jobId)}`;

// Rulesets
export const GET_RULESET = (rulesetType, partnerQuery = '') =>
  `/api/rule-sets/ruleset-type/${rulesetType}${partnerQuery}`;
export const CREATE_RULESET = '/api/rule-sets';
export const UPDATE_RULESET = (rulesetId) => `/api/rule-sets/${rulesetId}`;
export const PARTNER_SYNC = '/api/rule-sets/partner-sync';
export const GET_PARTNER_WORKFLOWS = (query = '') =>
  `/api/organization/partner-workflows${query}`;
export const GET_PARTNER_WORKFLOW = (workflowId) =>
  `/api/organization/partner-workflows/${workflowId}`;
export const GET_WELCOME_MESSAGE_CONFIG = (lobId, caseType) => {
  const params = new URLSearchParams();
  if (lobId) params.set('lobId', lobId);
  if (caseType) params.set('caseType', caseType);
  const query = params.toString();
  return `/api/rule-sets/welcome-message-config${query ? `?${query}` : ''}`;
};

// Analytics
export const ANALYTICS_LOOKER_URL = (dashboardName, timezone, locale) =>
  `/api/analytics/looker/${dashboardName}/${timezone}/${locale}`;
export const ANALYTICS_IFRAME_LOOK_URL = (caseId, lookNumber, timezone) =>
  `/api/analytics/iframelook/${caseId}/${lookNumber}/${timezone}`;
export const ANALYTICS_GET_REPORTS = (organization) =>
  `/api/analytics/${organization}/reports`;
export const ANALYTICS_DATA_DOWNLOAD_ENABLED = () =>
  '/api/analytics/data-download/enabled';

// New Media
export const GET_SIGNED_URL_FOR_MEDIA_UPLOAD = (ext, fileType, fileSizeLimit) =>
  `/api/newmedia/preSignedPost?ext=${ext}&fileType=${fileType}&fileSizeLimit=${fileSizeLimit}`;
export const GET_PUBLIC_SIGNED_URL_FOR_MEDIA_UPLOAD = (
  ext,
  fileType,
  fileSizeLimit,
) =>
  `/api/newmedia/publicPreSignedPost?ext=${ext}&fileType=${fileType}&fileSizeLimit=${fileSizeLimit}`;
export const GET_MEDIA_VIRUS_SCAN_STATUS = (key) =>
  `/api/newmedia/getMediaVirusScanStatus?key=${key}`;
export const GET_SHORTENED_PUBLIC_URL_FOR_GET = (longUrl, key) =>
  `/api/newmedia/getShortenedPublicUrlForGet?longUrl=${longUrl}&key=${key}`;
export const GET_SHORT_CODE_MEDIA = (code) =>
  `api/newmedia/getShortCodeMedia?code=${code}`; // different from format below because we use window.location to prepend
export const REDACT_IMAGE_ENDPOINT = (key) => `/api/newmedia/${key}/redactions`;

// Organizations
export const GET_ORGANIZATION_ROLES = (organizationId) =>
  `/api/organization/${organizationId}/roles`;
export const UPDATE_ORGANIZATION_BRANDING = (organizationId) =>
  `/api/organization/${organizationId}/branding`;
export const UPDATE_ORG_SETTING = (organizationId) =>
  `/api/organization/${organizationId}/settings`;
export const UPDATE_LINE_OF_BUSINESS = (organizationId, lineOfBusinessId) =>
  `/api/organization/${organizationId}/lineOfBusiness/${lineOfBusinessId}`;
export const GET_FULL_ORG_DATA = (organizationId) =>
  `/api/organization/${organizationId}/fullData`;
export const GET_ORG_DATA = (organizationId) =>
  `/api/organization/${organizationId}`;
export const GET_ORGANIZATION_FEATURES = (scope = 'public') =>
  `/api/organization/features?scope=${encodeURIComponent(scope)}`;
export const GET_EFFECTIVE_FEATURE_MAP = () =>
  '/api/organization/features/effective';
export const GET_CA_READINESS = () => '/api/organization/ca-readiness';
export const GET_ORGANIZATION_FEATURE_AUDIT_LOG = (queryString = '') =>
  `/api/organization/features/audit-log${queryString ? `?${queryString}` : ''}`;
export const UPDATE_ORGANIZATION_FEATURE = () => '/api/organization/features';
export const UPDATE_AUTO_RESEND_WELCOME_SETTINGS = () =>
  '/api/organization/auto-resend-welcome-settings';
export const UPDATE_QUIET_HOURS_SETTINGS = () =>
  '/api/organization/quiet-hours-settings';
export const GET_ORGANIZATION_ENTITLEMENTS = () =>
  '/api/organization/entitlements';
export const UPDATE_ORGANIZATION_ENTITLEMENTS = () =>
  '/api/organization/entitlements';

// Workflow Settings
export const GET_WORKFLOW_KEYWORDS = () =>
  '/api/organization/workflow-keywords';
export const GET_WORKFLOW_SETTINGS = (view) =>
  `/api/organization/workflow-settings${view ? `?view=${view}` : ''}`;
export const DELETE_WORKFLOW_SETTINGS = (workflowName) =>
  `/api/organization/workflow-settings/${workflowName}`;
export const CREATE_WORKFLOW_SETTINGS = () =>
  '/api/organization/workflow-settings';
export const UPDATE_WORKFLOW_SETTINGS = (workflowName) =>
  `/api/organization/workflow-settings/${workflowName}`;
export const CREATE_WORKFLOW_KEYWORD = () =>
  '/api/organization/workflow-keywords';

// Exposures
export const GET_EXPOSURES = '/api/exposures';
export const CREATE_EXPOSURE_CODE = '/api/exposures/codes';
export const CREATE_EXPOSURE_CODES = (typeId) =>
  `/api/exposures/types/${typeId}/codes`;
export const MODIFY_EXPOSURE_CODE = (codeId) =>
  `/api/exposures/codes/${codeId}`;
export const GET_EXPOSURE_CODE_CASE_COUNT = (codeId) =>
  `/api/exposures/codes/${codeId}/case-count`;
export const MODIFY_EXPOSURE_TYPE = (typeId) =>
  `/api/exposures/types/${typeId}`;

// Queue
export const GET_QUEUES = '/api/queues';
export const GET_ASSIGNED_QUEUES = '/api/queues/assigned';
export const QUERY_QUEUE_DETAILS = '/api/queues/query';
export const UPDATE_QUEUE = (queueId) => `/api/queues/${queueId}`;
export const QUICK_ASSIGN_USER_QUEUE_CASE = (operatorId) =>
  `/api/operators/${operatorId}/assigned-cases`;
export const MODIFY_QUEUE_ITEM = (queueId, caseId) =>
  `/api/queues/${queueId}/items/${caseId}`;
export const GET_QUEUE_METRICS = (queueId) => `/api/queues/${queueId}/metrics`;
export const GET_QUEUE_DEACTIVATION_PREVIEW = (queueId) =>
  `/api/queues/${queueId}/deactivation-preview`;
export const DEACTIVATE_QUEUE = (queueId) =>
  `/api/queues/${queueId}/deactivate`;

// groups
export const GET_GROUPS = '/api/groups';
export const GET_GROUP_MEMBERS = (groupId) => `/api/groups/${groupId}/members`;

// Admin routes
export const UPDATE_USER_ROLES = (userId) => `/api/admin/${userId}/roles`;
export const PATCH_OPERATOR = (operatorId) => `/api/operators/${operatorId}`;

// Push Notifications
export const CLEAR_USER_PUSH_NOTIFICATIONS_FOR_DEVICE_ENDPOINT =
  '/api/push-notifications/unregister';

// partner integrations
export const GET_INTEGRATION_CONFIG = (integrationName) =>
  `/api/organization/integrationConfig/${integrationName}`;
export const GET_INTEGRATION_PARTNERS = () =>
  `/api/organization/integration-partners`;
export const CREATE_INTEGRATION_CONFIG = () =>
  '/api/organization/integrationConfig';
export const DELETE_INTEGRATION_CONFIG = (partnerName, integrationId) =>
  `/api/organization/integrationConfig/${partnerName}/integrationId/${integrationId}`;
export const UPDATE_INTEGRATION_CONFIG = (overrideRuleset) =>
  `/api/organization/integrationConfig${
    overrideRuleset ? '?overrideRuleset=true' : ''
  }`;

// Auto Close Config
export const GET_AUTO_CLOSE_CONFIGS = (partnerQuery = '') =>
  `/api/auto-close-config${partnerQuery}`;
export const GET_COMPOSITE_AUTO_CLOSE_CONFIGS = (partnerQuery = '') =>
  `/api/auto-close-config/composite${partnerQuery}`;
export const CREATE_AUTO_CLOSE_CONFIG = '/api/auto-close-config/';
export const UPDATE_AUTO_CLOSE_CONFIG = (configId) =>
  `/api/auto-close-config/${configId}`;

// Workflow initiation (HMB-15123)
export const GET_WORKFLOW_INSTANCES = (caseId, { initiation } = {}) =>
  `/api/job/${caseId}/workflow-instances${initiation ? `?initiation=${initiation}` : ''}`;
export const GET_WORKFLOW_FORM_DATA = (
  caseId,
  partnerName,
  partnerWorkflowId,
) =>
  `/api/job/${caseId}/workflow-instances/${partnerName}/${partnerWorkflowId}/form-data`;
export const CREATE_WORKFLOW_INSTANCE = (caseId) =>
  `/api/job/${caseId}/workflow-instances`;

export const CAMPAIGNS = (query = '') => `/api/campaigns${query}`;
export const CAMPAIGN = (campaignId) => `${CAMPAIGNS()}/${campaignId}`;
const CAMPAIGN_BULK_SEND = (campaignId, campaignBulkSendId) =>
  `${CAMPAIGN(campaignId)}/campaign-bulk-sends/${campaignBulkSendId}`;

export const CAMPAIGN_DELIVERY_REPORT = (campaignId) =>
  `${CAMPAIGN(campaignId)}/delivery-report`;
export const CAMPAIGN_CONTACT_UPLOAD = (campaignId, campaignBulkSendId) =>
  `${CAMPAIGN_BULK_SEND(campaignId, campaignBulkSendId)}/contacts`;
export const CAMPAIGN_CONTACTS = (campaignId, campaignBulkSendId, limit) =>
  `${CAMPAIGN_BULK_SEND(campaignId, campaignBulkSendId)}/contacts?limit=${limit}`;
export const CAMPAIGN_VALIDATION_REPORT = (campaignId, campaignBulkSendId) =>
  `${CAMPAIGN_BULK_SEND(campaignId, campaignBulkSendId)}/contacts/validation-report`;
export const CAMPAIGN_BULK_SEND_SCHEDULE = (campaignId, campaignBulkSendId) =>
  `${CAMPAIGN_BULK_SEND(campaignId, campaignBulkSendId)}/schedule`;

export const GET_BRANDS = '/api/brands';

/**
 * NOTE: When adding endpoints to this file, be sure to prepend each with a '/'. [Ex: '/api/endpoint' not 'api/endpoint']
 * Otherwise when trying to make a call to the endpoint, whatever path may already be in the URL may be added along with
 * your endpoint, to the host URL.
 * */
